import { useEffect, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api";
import type { Provider } from "@/lib/types";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Edit2, X, Plus, Key, Link as LinkIcon, Eye, Trash2, Wand2 } from "lucide-react";
import { Link } from "wouter";
import { Checkbox } from "@/components/ui/checkbox";

type ModalMode =
  | { type: "closed" }
  | { type: "create" }
  | { type: "edit"; provider: Provider }
  | { type: "revealKey"; provider: Provider; keyStr: string };

export default function AdminProviders() {
  const { t } = useLanguage();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>({ type: "closed" });

  const refresh = async () => {
    try {
      const data = await adminApi.getProviders();
      setProviders(data);
    } catch (e: any) {
      toast.error(e.message || "Failed to load providers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this provider? Models under it will fail.")) return;
    try {
      await adminApi.deactivateProvider(id);
      toast.success("Provider deactivated");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Failed to deactivate");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("admin.provider.confirmDelete"))) return;
    try {
      await adminApi.deleteProvider(id);
      toast.success(t("admin.provider.deleteSuccess"));
      refresh();
    } catch (e: any) {
      toast.error(e.message || t("admin.provider.deleteFailed"));
    }
  };

  const handleRevealKey = async (provider: Provider) => {
    try {
      const keyStr = await adminApi.revealProviderKey(provider.id);
      setModal({ type: "revealKey", provider, keyStr });
    } catch (e: any) {
      toast.error(e.message || "Failed to reveal key");
    }
  };

  return (
    <ConsoleLayout title="Providers">
      <div className="flex flex-col h-full bg-background relative">
        <div className="p-6 overflow-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                Providers
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage upstream API providers (e.g., OpenAI, Anthropic) and their credentials.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link href="/console/admin/providers/new">
                <Button variant="secondary">
                  <Wand2 className="w-4 h-4 mr-2" />
                  New Provider (Wizard)
                </Button>
              </Link>
              <Button onClick={() => setModal({ type: "create" })}>
                <Plus className="w-4 h-4 mr-2" />
                New Provider
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Slug</th>
                    <th className="px-4 py-3 font-medium">Backend</th>
                    <th className="px-4 py-3 font-medium">Base URL</th>
                    <th className="px-4 py-3 font-medium">Protocols</th>
                    <th className="px-4 py-3 font-medium">API Key</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        Loading...
                      </td>
                    </tr>
                  ) : providers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        No providers found.
                      </td>
                    </tr>
                  ) : (
                    providers.map((p) => (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">
                          {p.name}
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                            {p.slug}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          {p.backend === "vertex" ? (
                            <span className="text-[10px] bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                              Vertex
                            </span>
                          ) : p.backend === "bedrock" ? (
                            <span className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                              Bedrock
                            </span>
                          ) : (
                            <span className="text-[10px] bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                              Direct
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[200px] truncate text-muted-foreground">
                          {p.backend === "vertex" ? (
                            <span className="text-xs">{p.extra_config?.project} ({p.extra_config?.location})</span>
                          ) : p.backend === "bedrock" ? (
                            <span className="text-xs">{p.extra_config?.region}</span>
                          ) : (
                            p.base_url
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                            {p.protocol}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {p.has_api_key ? (
                            <span className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/50 px-2 py-1 rounded-full">
                              Configured
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 px-2 py-1 rounded-full">
                              Missing
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full ${
                              p.is_active
                                ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50"
                                : "text-muted-foreground bg-muted"
                            }`}
                          >
                            {p.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link href={`/console/admin/providers/${p.id}/models`}>
                              <Button variant="ghost" size="icon" title="Manage Models">
                                <LinkIcon className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </Link>
                            {p.has_api_key && (
                              <Button variant="ghost" size="icon" title="Reveal Key" onClick={() => handleRevealKey(p)}>
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Edit Provider"
                              onClick={() => setModal({ type: "edit", provider: p })}
                            >
                              <Edit2 className="w-4 h-4 text-muted-foreground" />
                            </Button>
                            {p.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Deactivate"
                                onClick={() => handleDeactivate(p.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive/70 hover:text-destructive" />
                              </Button>
                            )}
                            {!p.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t("admin.common.delete")}
                                onClick={() => handleDelete(p.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {modal.type !== "closed" && (
        <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border shadow-xl rounded-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/20">
              <h3 className="font-semibold text-lg">
                {modal.type === "create" && "New Provider"}
                {modal.type === "edit" && "Edit Provider"}
                {modal.type === "revealKey" && "Reveal API Key"}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setModal({ type: "closed" })}
                className="-mr-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {(modal.type === "create" || modal.type === "edit") && (
              <ProviderForm
                mode={modal.type}
                provider={modal.type === "edit" ? modal.provider : undefined}
                onSuccess={() => {
                  setModal({ type: "closed" });
                  refresh();
                }}
              />
            )}

            {modal.type === "revealKey" && (
              <div className="p-6">
                <p className="text-sm text-muted-foreground mb-4">
                  This is the upstream API key for <strong>{modal.provider.name}</strong>. It will only be shown once.
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={modal.keyStr} className="font-mono text-xs" />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(modal.keyStr);
                      toast.success("Copied to clipboard");
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={() => setModal({ type: "closed" })}>Close</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ConsoleLayout>
  );
}

function ProviderForm({
  mode,
  provider,
  onSuccess,
}: {
  mode: "create" | "edit";
  provider?: Provider;
  onSuccess: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [protocol, setProtocol] = useState<string>(provider?.protocol || "anthropic");
  const [backend, setBackend] = useState<"direct" | "vertex" | "bedrock">(
    (provider?.backend as "direct" | "vertex" | "bedrock") || "direct"
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data: any = {
      name: fd.get("name") as string,
      slug: fd.get("slug") as string,
      protocol: fd.get("protocol") as string,
      backend: backend,
    };
    
    if (backend === "direct") {
      data.base_url = fd.get("base_url") as string;
      const k = fd.get("api_key") as string;
      if (mode === "create" || k) data.api_key = k;
    } else if (backend === "vertex") {
      data.extra_config = {
        project: fd.get("project") as string,
        location: fd.get("location") as string,
      };
      const sa = fd.get("google_service_account_json") as string;
      if (mode === "create" || sa) {
        try {
          JSON.parse(sa);
          data.google_service_account_json = sa;
        } catch {
          toast.error("Service Account JSON must be valid JSON");
          return;
        }
      }
    } else {
      data.extra_config = { region: fd.get("region") as string };
      const accessKeyId = (fd.get("aws_access_key_id") as string)?.trim();
      const secretAccessKey = (fd.get("aws_secret_access_key") as string)?.trim();
      if (mode === "create" || accessKeyId || secretAccessKey) {
        if (!accessKeyId || !secretAccessKey) {
          toast.error("AWS Access Key ID and Secret Access Key are both required");
          return;
        }
        data.aws_credentials_json = JSON.stringify({
          access_key_id: accessKeyId,
          secret_access_key: secretAccessKey,
        });
      }
    }

    if (!data.protocol) {
      toast.error("Please select a protocol");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "create") {
        await adminApi.createProvider(data);
        toast.success("Provider created");
      } else {
        await adminApi.updateProvider(provider!.id, data);
        toast.success("Provider updated");
      }
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="space-y-2">
        <Label>Name</Label>
        <Input name="name" defaultValue={provider?.name} required placeholder="e.g. OpenAI" />
      </div>
      <div className="space-y-2">
        <Label>Slug</Label>
        <Input
          name="slug"
          defaultValue={provider?.slug}
          required
          placeholder="e.g. openai"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">Unique identifier used for API routing</p>
      </div>
      <div className="space-y-2 pt-2">
        <Label>Protocol</Label>
        <select
          name="protocol"
          value={protocol}
          onChange={(e) => {
            const p = e.target.value;
            setProtocol(p);
            if (p !== "anthropic") setBackend("direct");
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="anthropic">Anthropic</option>
          <option value="openai-chat">OpenAI Chat</option>
          <option value="openai-response">OpenAI Response</option>
        </select>
        <p className="text-xs text-muted-foreground">The single protocol this provider speaks.</p>
      </div>

      {protocol === "anthropic" && (
        <div className="space-y-2 pt-2">
          <Label>Backend</Label>
          <select
            value={backend}
            onChange={(e) => setBackend(e.target.value as "direct" | "vertex" | "bedrock")}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="direct">Direct (first-party API)</option>
            <option value="vertex">Google Vertex AI</option>
            <option value="bedrock">AWS Bedrock</option>
          </select>
          <p className="text-xs text-muted-foreground">How this provider connects to the upstream service.</p>
        </div>
      )}

      {backend === "direct" ? (
        <>
          <div className="space-y-2">
            <Label>Base URL</Label>
            <Input
              name="base_url"
              defaultValue={provider?.base_url}
              required
              placeholder="https://api.anthropic.com/v1"
            />
            <p className="text-xs text-muted-foreground">Include the version path (e.g. /v1); the endpoint suffix is appended automatically.</p>
          </div>
          <div className="space-y-2">
            <Label>{mode === "edit" ? "API Key (Optional)" : "API Key"}</Label>
            <Input
              name="api_key"
              type="password"
              placeholder={mode === "edit" ? "Leave blank to keep current key" : "sk-..."}
              required={mode === "create"}
            />
          </div>
        </>
      ) : backend === "vertex" ? (
        <>
          <div className="space-y-2">
            <Label>GCP Project ID</Label>
            <Input
              name="project"
              defaultValue={provider?.extra_config?.project}
              required
              placeholder="e.g. my-google-project"
            />
          </div>
          <div className="space-y-2">
            <Label>Location</Label>
            <Input
              name="location"
              defaultValue={provider?.extra_config?.location}
              required
              placeholder="e.g. us-central1"
            />
          </div>
          <div className="space-y-2">
            <Label>{mode === "edit" ? "Service Account JSON (Optional)" : "Service Account JSON"}</Label>
            <textarea
              name="google_service_account_json"
              placeholder={mode === "edit" ? "Leave blank to keep current key" : `{
  "type": "service_account",
  ...`}
              required={mode === "create"}
              className="w-full min-h-[150px] font-mono text-xs p-3 rounded-md border border-input bg-background"
              autoComplete="off"
            />
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <Label>AWS Region</Label>
            <Input
              name="region"
              defaultValue={provider?.extra_config?.region}
              required
              placeholder="us-east-1"
            />
          </div>
          <div className="space-y-2">
            <Label>{mode === "edit" ? "AWS Access Key ID (Optional)" : "AWS Access Key ID"}</Label>
            <Input
              name="aws_access_key_id"
              required={mode === "create"}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label>{mode === "edit" ? "AWS Secret Access Key (Optional)" : "AWS Secret Access Key"}</Label>
            <Input
              name="aws_secret_access_key"
              type="password"
              required={mode === "create"}
              placeholder={mode === "edit" ? "Leave blank to keep current credentials" : "••••••••••••••••"}
              autoComplete="off"
            />
          </div>
        </>
      )}

      <div className="pt-4 flex justify-end gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving..." : mode === "create" ? "Create Provider" : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}
